import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Gift, Award, GraduationCap, Heart, Brain, ShieldCheck, Trophy, Layers,
  Plus, Trash2, Check, X, Sparkles, Eye, Send, FileText, Download
} from "lucide-react";

const TABS = [
  { id: "cral", label: "CRAL & Buoni", icon: Gift, color: "#F472B6" },
  { id: "loyalty", label: "Carta Fedeltà", icon: Award, color: "#EAB308" },
  { id: "achievements", label: "Gamification", icon: Trophy, color: "#10B981" },
  { id: "training", label: "Formazione", icon: GraduationCap, color: "#8B5CF6" },
  { id: "wellbeing", label: "Wellbeing", icon: Heart, color: "#EC4899" },
  { id: "perfreview", label: "AI Review 1:1", icon: Brain, color: "#6366F1" },
  { id: "whistleblowing", label: "Whistleblowing", icon: ShieldCheck, color: "#0F766E" },
  { id: "blockchain", label: "Blockchain", icon: Layers, color: "#0EA5E9" },
];

export default function AdvancedFeaturesPage() {
  const [tab, setTab] = useState("cral");
  const T = TABS.find(t => t.id === tab);
  return (
    <div className="p-5 md:p-8 max-w-[1400px] mx-auto" data-testid="advanced-features-page">
      <div className="mb-6 md:mb-8">
        <div className="text-[11px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: T.color }}>— Funzioni Premium</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Strumenti avanzati HR</h1>
        <p className="mt-2 text-slate-600 max-w-[640px]">Welfare, gamification, formazione obbligatoria, wellbeing pulse, AI review, whistleblowing GDPR e blockchain — in un'unica console.</p>
      </div>

      <div className="grid grid-cols-12 gap-5 md:gap-8">
        {/* LEFT VERTICAL TAB LIST (desktop) */}
        <aside className="hidden md:block col-span-3 lg:col-span-3" data-testid="advanced-sidebar">
          <div className="sticky top-6 rounded-2xl p-3 bg-white border border-slate-200" style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
            <div className="text-[10.5px] tracking-[0.22em] uppercase font-bold px-3 pt-2 pb-3" style={{ color: "#94A3B8" }}>Sezioni</div>
            <nav className="space-y-1" data-testid="advanced-tabs">
              {TABS.map(t => {
                const Icon = t.icon; const active = t.id === tab;
                return (
                  <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all text-left"
                    style={{
                      background: active ? `linear-gradient(135deg, ${t.color}18 0%, ${t.color}08 100%)` : "transparent",
                      color: active ? "#0F172A" : "#475569",
                      border: active ? `1px solid ${t.color}40` : "1px solid transparent",
                    }}>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: active ? `linear-gradient(135deg, ${t.color} 0%, ${t.color}CC 100%)` : `${t.color}15` }}>
                      <Icon className="w-4 h-4" style={{ color: active ? "#FFFFFF" : t.color }} />
                    </span>
                    <span className="flex-1 truncate">{t.label}</span>
                    {active && <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* MOBILE: horizontal scrollable tabs */}
        <div className="md:hidden col-span-12 -mx-5 px-5 overflow-x-auto pb-2">
          <div className="flex gap-2">
            {TABS.map(t => {
              const Icon = t.icon; const active = t.id === tab;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-mobile-${t.id}`}
                  className="shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold transition-all"
                  style={{
                    background: active ? `linear-gradient(135deg, ${t.color} 0%, ${t.color}DD 100%)` : "#FFFFFF",
                    color: active ? "#FFFFFF" : "#0F172A",
                    border: active ? "none" : "1px solid rgba(15,23,42,0.10)",
                  }}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* MAIN PANEL */}
        <div className="col-span-12 md:col-span-9 lg:col-span-9" data-testid={`panel-${tab}`}>
          {tab === "cral" && <CralPanel />}
          {tab === "loyalty" && <LoyaltyPanel />}
          {tab === "achievements" && <AchievementsPanel />}
          {tab === "training" && <TrainingPanel />}
          {tab === "wellbeing" && <WellbeingPanel />}
          {tab === "perfreview" && <PerfReviewPanel />}
          {tab === "whistleblowing" && <WhistleblowingPanel />}
          {tab === "blockchain" && <BlockchainPanel />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- CRAL ---------------- */
function CralPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("fitness");
  const [discount, setDiscount] = useState(15);
  const [desc, setDesc] = useState("");
  const load = async () => {
    try { setItems((await api.get("/cral/partners")).data.items || []); } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name.trim()) return toast.error("Nome obbligatorio");
    try {
      await api.post("/cral/partners", { name, category, discount_pct: Number(discount), description: desc });
      toast.success("Partner creato"); setName(""); setDesc(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => {
    if (!window.confirm("Disattivare questo partner?")) return;
    try { await api.delete(`/cral/partners/${id}`); toast.success("Disattivato"); load(); } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-white border border-slate-200" data-testid="cral-create-form">
        <h3 className="text-lg font-bold mb-4">Nuovo partner welfare</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Nome partner (es. Decathlon)" data-testid="cral-name" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <select value={category} onChange={e=>setCategory(e.target.value)} data-testid="cral-category" className="px-4 h-11 rounded-xl border border-slate-200 text-sm">
            <option value="fitness">Fitness</option>
            <option value="food">Food</option>
            <option value="travel">Travel</option>
            <option value="shopping">Shopping</option>
            <option value="wellness">Wellness</option>
            <option value="culture">Cultura</option>
          </select>
          <input type="number" min={1} max={99} value={discount} onChange={e=>setDiscount(e.target.value)} placeholder="Sconto %" data-testid="cral-discount" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <Button onClick={create} data-testid="cral-create-btn" className="rounded-xl h-11 bg-pink-500 hover:bg-pink-600"><Plus className="w-4 h-4 mr-1"/> Aggiungi</Button>
        </div>
        <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Descrizione breve" data-testid="cral-desc" className="mt-3 w-full px-4 h-11 rounded-xl border border-slate-200 text-sm" />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cral-list">
        {items.map(p => (
          <div key={p.id} className="rounded-2xl p-5 bg-white border border-slate-200" data-testid={`partner-${p.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-pink-50 text-pink-700">{p.category}</div>
              <button onClick={()=>del(p.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
            </div>
            <h4 className="text-lg font-bold">{p.name}</h4>
            <div className="text-2xl font-extrabold text-emerald-600 mt-1">-{p.discount_pct}%</div>
            {p.description && <p className="text-xs text-slate-500 mt-2">{p.description}</p>}
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center text-slate-500 py-8">Nessun partner. Aggiungine uno!</div>}
      </div>
    </div>
  );
}

/* ---------------- LOYALTY ---------------- */
function LoyaltyPanel() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [points, setPoints] = useState(27);
  const [reason, setReason] = useState("Mese completo presenza");
  const load = async () => {
    try {
      setLeaderboard((await api.get("/loyalty/leaderboard")).data.items || []);
      const ed = (await api.get("/employees")).data;
      setEmployees(Array.isArray(ed) ? ed : ed.items || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const award = async () => {
    if (!empId) return toast.error("Seleziona dipendente");
    try {
      await api.post("/loyalty/award", { employee_id: empId, points: Number(points), reason });
      toast.success(`Assegnati ${points} punti`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Assegna punti fedeltà</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <select value={empId} onChange={e=>setEmpId(e.target.value)} data-testid="loyalty-emp" className="px-4 h-11 rounded-xl border border-slate-200 text-sm">
            <option value="">Seleziona dipendente</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
          <input type="number" value={points} onChange={e=>setPoints(e.target.value)} data-testid="loyalty-points" placeholder="Punti" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <input value={reason} onChange={e=>setReason(e.target.value)} data-testid="loyalty-reason" placeholder="Motivazione" className="px-4 h-11 rounded-xl border border-slate-200 text-sm md:col-span-1" />
          <Button onClick={award} data-testid="loyalty-award-btn" className="rounded-xl h-11 bg-yellow-500 hover:bg-yellow-600">Assegna</Button>
        </div>
      </div>
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Top 20 Leaderboard punti</h3>
        <div className="space-y-2" data-testid="loyalty-leaderboard">
          {leaderboard.map((row, i) => (
            <div key={row.employee.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shrink-0" style={{ background: i === 0 ? "#EAB308" : i === 1 ? "#94A3B8" : i === 2 ? "#B45309" : "#475569" }}>{i+1}</div>
              <div className="flex-1 font-semibold">{row.employee.full_name}</div>
              <div className="font-mono font-bold text-amber-600">{row.points} pt</div>
            </div>
          ))}
          {leaderboard.length === 0 && <div className="text-center text-slate-500 py-6">Nessuna transazione punti registrata</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ACHIEVEMENTS ---------------- */
function AchievementsPanel() {
  const [defs, setDefs] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        setDefs((await api.get("/achievements/defs")).data.items || []);
        setLeaderboard((await api.get("/achievements/leaderboard")).data.items || []);
      } catch (_) {}
    })();
  }, []);
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Badge disponibili ({defs.length})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="achievements-defs">
          {defs.map(a => (
            <div key={a.code} className="rounded-2xl p-4 border" style={{ borderColor: a.color + "40", background: a.color + "08" }}>
              <div className="text-2xl mb-2">🏆</div>
              <div className="font-bold">{a.title}</div>
              <div className="text-xs text-slate-600 mt-1">{a.desc}</div>
              <div className="text-xs font-bold mt-2" style={{ color: a.color }}>+{a.points} punti</div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Leaderboard achievement</h3>
        <div className="space-y-2">
          {leaderboard.map((row, i) => (
            <div key={row.employee.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50">
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm" style={{ background: i === 0 ? "#EAB308" : "#475569" }}>{i+1}</div>
              <div className="flex-1 font-semibold">{row.employee.full_name}</div>
              <div className="text-sm text-slate-500">{row.achievements} badge</div>
              <div className="font-mono font-bold text-emerald-600">{row.points} pt</div>
            </div>
          ))}
          {leaderboard.length === 0 && <div className="text-center text-slate-500 py-6">Nessun badge ancora sbloccato</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- TRAINING ---------------- */
function TrainingPanel() {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [title, setTitle] = useState("");
  const [cat, setCat] = useState("sicurezza");
  const [hours, setHours] = useState(4);
  const [expiry, setExpiry] = useState(60);
  const [selCourse, setSelCourse] = useState("");
  const [selEmps, setSelEmps] = useState([]);
  const load = async () => {
    try {
      setCourses((await api.get("/training/courses")).data.items || []);
      setEnrollments((await api.get("/training/enrollments")).data.items || []);
      setExpiring((await api.get("/training/expiring")).data.items || []);
      const ed = (await api.get("/employees")).data;
      setEmployees(Array.isArray(ed) ? ed : ed.items || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const createCourse = async () => {
    if (!title.trim()) return toast.error("Titolo obbligatorio");
    try {
      await api.post("/training/courses", { title, category: cat, mandatory: true, duration_h: Number(hours), expiry_months: Number(expiry) });
      toast.success("Corso creato"); setTitle(""); load();
    } catch (e) { toast.error("Errore"); }
  };
  const enroll = async () => {
    if (!selCourse || selEmps.length === 0) return toast.error("Seleziona corso e dipendenti");
    try {
      const r = await api.post("/training/enroll", { course_id: selCourse, employee_ids: selEmps });
      toast.success(`${r.data.created} iscrizioni create`); setSelEmps([]); load();
    } catch (e) { toast.error("Errore"); }
  };
  const exportInail = async () => {
    try {
      const r = await api.get("/training/export-inail", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "formazione_inail.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Nuovo corso obbligatorio</h3>
        <div className="grid md:grid-cols-5 gap-3">
          <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Titolo (es. Sicurezza Generale)" data-testid="course-title" className="md:col-span-2 px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <select value={cat} onChange={e=>setCat(e.target.value)} data-testid="course-cat" className="px-4 h-11 rounded-xl border border-slate-200 text-sm">
            <option value="sicurezza">Sicurezza</option><option value="antincorruzione">Anti-corruzione</option>
            <option value="gdpr">GDPR</option><option value="primo_soccorso">Primo soccorso</option>
            <option value="antincendio">Antincendio</option><option value="altro">Altro</option>
          </select>
          <input type="number" value={hours} onChange={e=>setHours(e.target.value)} placeholder="Ore" data-testid="course-hours" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <input type="number" value={expiry} onChange={e=>setExpiry(e.target.value)} placeholder="Validità mesi" data-testid="course-expiry" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
        </div>
        <Button onClick={createCourse} data-testid="course-create-btn" className="mt-3 rounded-xl bg-violet-500 hover:bg-violet-600"><Plus className="w-4 h-4 mr-1"/> Crea corso</Button>
      </div>

      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Iscrivi dipendenti a un corso</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <select value={selCourse} onChange={e=>setSelCourse(e.target.value)} data-testid="enroll-course" className="px-4 h-11 rounded-xl border border-slate-200 text-sm">
            <option value="">Seleziona corso</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <select multiple value={selEmps} onChange={e=>setSelEmps(Array.from(e.target.selectedOptions).map(o=>o.value))} data-testid="enroll-emps" className="px-4 py-2 rounded-xl border border-slate-200 text-sm min-h-[44px]">
            {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
          <Button onClick={enroll} data-testid="enroll-btn" className="rounded-xl bg-violet-500 hover:bg-violet-600">Iscrivi ({selEmps.length})</Button>
        </div>
      </div>

      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Iscrizioni ({enrollments.length})</h3>
          <Button onClick={exportInail} variant="outline" data-testid="export-inail" className="rounded-xl"><Download className="w-4 h-4 mr-1"/> Export INAIL CSV</Button>
        </div>
        <div className="space-y-1 text-sm" data-testid="enrollments-list">
          {enrollments.slice(0,30).map(e => (
            <div key={e.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
              <span>{e.course_title}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${e.status==='completed'?'bg-emerald-100 text-emerald-700':'bg-slate-200 text-slate-600'}`}>{e.status}</span>
            </div>
          ))}
        </div>
      </div>

      {expiring.length > 0 && (
        <div className="rounded-2xl p-6 border border-amber-300 bg-amber-50">
          <h3 className="text-lg font-bold text-amber-900 mb-3">⚠️ Scadono entro 30 giorni ({expiring.length})</h3>
          {expiring.map(e => (
            <div key={e.id} className="text-sm py-1 text-amber-900">• {e.course_title} — scade {e.expires_at?.slice(0,10)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- WELLBEING ---------------- */
function WellbeingPanel() {
  const [heatmap, setHeatmap] = useState([]);
  const [alert, setAlert] = useState(null);
  const [comments, setComments] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const h = (await api.get("/wellbeing/heatmap")).data;
        setHeatmap(h.items || []); setAlert(h.alert);
        setComments((await api.get("/wellbeing/comments")).data.items || []);
      } catch (_) {}
    })();
  }, []);
  const moodColor = (v) => v >= 4 ? "#10B981" : v >= 3 ? "#F59E0B" : "#EF4444";
  return (
    <div className="space-y-6">
      {alert && <div className="p-4 rounded-2xl bg-red-50 border border-red-300 text-red-900 font-semibold">⚠️ {alert}</div>}
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Heatmap ultime 8 settimane</h3>
        <div className="overflow-x-auto" data-testid="wellbeing-heatmap">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Settimana</th><th>Risposte</th><th>Umore</th><th>Stress</th><th>Carico</th><th>Team</th></tr></thead>
            <tbody>
              {heatmap.map(r => (
                <tr key={r.week} className="border-t border-slate-100">
                  <td className="py-3 font-mono">{r.week}</td>
                  <td>{r.count}</td>
                  <td><span className="px-2 py-0.5 rounded-full text-white text-xs font-bold" style={{ background: moodColor(r.mood) }}>{r.mood}</span></td>
                  <td><span className="px-2 py-0.5 rounded-full text-white text-xs font-bold" style={{ background: moodColor(6-r.stress) }}>{r.stress}</span></td>
                  <td>{r.workload}</td>
                  <td>{r.team_feel}</td>
                </tr>
              ))}
              {heatmap.length === 0 && <tr><td colSpan="6" className="text-center text-slate-500 py-6">Nessuna risposta ancora ricevuta</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Commenti anonimi recenti</h3>
        <div className="space-y-2" data-testid="wellbeing-comments">
          {comments.map((c, i) => (
            <div key={i} className="p-3 rounded-xl bg-slate-50 text-sm">
              <div className="text-xs text-slate-500 mb-1">{c.week} · umore {c.mood} · stress {c.stress}</div>
              "{c.comment}"
            </div>
          ))}
          {comments.length === 0 && <div className="text-center text-slate-500 py-6">Nessun commento anonimo</div>}
        </div>
      </div>
    </div>
  );
}

/* ---------------- PERF REVIEW ---------------- */
function PerfReviewPanel() {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [period, setPeriod] = useState("Q1 2026");
  const [focus, setFocus] = useState("");
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = async () => {
    try {
      setReviews((await api.get("/perf-review")).data.items || []);
      const ed = (await api.get("/employees")).data;
      setEmployees(Array.isArray(ed) ? ed : ed.items || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (!empId) return toast.error("Seleziona dipendente");
    setLoading(true);
    try {
      await api.post("/perf-review/generate", { employee_id: empId, period, focus: focus || null });
      toast.success("Review generata da GPT-5.2"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  const summarize = async (rid) => {
    setLoading(true);
    try {
      await api.post(`/perf-review/${rid}/summary`);
      toast.success("Sintesi AI generata"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-indigo-500"/> Genera review 1:1 con GPT-5.2</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <select value={empId} onChange={e=>setEmpId(e.target.value)} data-testid="pr-emp" className="px-4 h-11 rounded-xl border border-slate-200 text-sm">
            <option value="">Dipendente</option>
            {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
          <input value={period} onChange={e=>setPeriod(e.target.value)} data-testid="pr-period" placeholder="Periodo (Q1 2026)" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <input value={focus} onChange={e=>setFocus(e.target.value)} data-testid="pr-focus" placeholder="Focus (opzionale)" className="px-4 h-11 rounded-xl border border-slate-200 text-sm" />
          <Button onClick={generate} disabled={loading} data-testid="pr-generate-btn" className="rounded-xl bg-indigo-500 hover:bg-indigo-600">{loading ? "..." : "Genera"}</Button>
        </div>
      </div>
      <div className="space-y-3" data-testid="pr-list">
        {reviews.map(r => (
          <div key={r.id} className="rounded-2xl p-5 bg-white border border-slate-200">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-bold">{r.employee_name} · {r.period}</div>
                <div className="text-xs text-slate-500">Stato: {r.status} · {r.questions?.length || 0} domande</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={()=>setOpenId(openId===r.id?null:r.id)} variant="outline" className="rounded-xl"><Eye className="w-4 h-4 mr-1"/> Dettagli</Button>
                {r.status === "answered" && <Button onClick={()=>summarize(r.id)} disabled={loading} className="rounded-xl bg-indigo-500"><Brain className="w-4 h-4 mr-1"/> Sintetizza AI</Button>}
              </div>
            </div>
            {openId === r.id && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-3 text-sm">
                {r.questions?.map((q, i) => (
                  <div key={i}>
                    <div className="font-semibold">Q{i+1}: {q}</div>
                    {r.answers?.[i] && <div className="mt-1 text-slate-700 pl-3 border-l-2 border-indigo-300">{r.answers[i]}</div>}
                  </div>
                ))}
                {r.ai_summary && (
                  <div className="mt-3 p-3 rounded-xl bg-indigo-50 border border-indigo-200 whitespace-pre-wrap text-sm">{r.ai_summary}</div>
                )}
              </div>
            )}
          </div>
        ))}
        {reviews.length === 0 && <div className="text-center text-slate-500 py-8">Nessuna review generata</div>}
      </div>
    </div>
  );
}

/* ---------------- WHISTLEBLOWING ---------------- */
function WhistleblowingPanel() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");
  const load = async () => {
    try { setItems((await api.get("/whistleblowing")).data.items || []); } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/whistleblowing/${id}`, { status, note: note || null });
      toast.success("Aggiornato"); setNote(""); load();
    } catch (e) { toast.error("Errore"); }
  };
  return (
    <div className="space-y-3" data-testid="wb-list">
      {items.map(r => (
        <div key={r.id} className="rounded-2xl p-5 bg-white border border-slate-200">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-slate-100">{r.tracking_code}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${r.status==='open'?'bg-amber-100 text-amber-700':r.status==='resolved'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-600'}`}>{r.status}</span>
              </div>
              <div className="font-bold text-lg mt-1">{r.subject}</div>
              <div className="text-xs text-slate-500">{r.category} · {new Date(r.submitted_at).toLocaleString("it-IT")}</div>
            </div>
            <Button onClick={()=>setOpenId(openId===r.id?null:r.id)} variant="outline" className="rounded-xl"><Eye className="w-4 h-4 mr-1"/> Vedi</Button>
          </div>
          {openId === r.id && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <div className="p-3 rounded-xl bg-slate-50 text-sm whitespace-pre-wrap">{r.body}</div>
              {r.contact_email && <div className="text-xs text-slate-600">📧 Contatto: <a href={`mailto:${r.contact_email}`} className="underline">{r.contact_email}</a></div>}
              {r.admin_notes?.length > 0 && (
                <div className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Note admin</div>
                  {r.admin_notes.map((n, i) => <div key={i} className="text-xs p-2 bg-slate-100 rounded">[{n.at?.slice(0,16)}] {n.by}: {n.note}</div>)}
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <input value={note} onChange={e=>setNote(e.target.value)} placeholder="Aggiungi nota interna" className="flex-1 px-3 h-10 rounded-xl border border-slate-200 text-sm" />
                <Button onClick={()=>updateStatus(r.id, "investigating")} className="rounded-xl bg-blue-500">In esame</Button>
                <Button onClick={()=>updateStatus(r.id, "resolved")} className="rounded-xl bg-emerald-500">Risolta</Button>
                <Button onClick={()=>updateStatus(r.id, "dismissed")} className="rounded-xl bg-slate-500">Archivia</Button>
              </div>
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && <div className="text-center text-slate-500 py-8">Nessuna segnalazione ricevuta</div>}
    </div>
  );
}

/* ---------------- BLOCKCHAIN ---------------- */
function BlockchainPanel() {
  const [chain, setChain] = useState([]);
  const load = async () => {
    try { setChain((await api.get("/blockchain/chain")).data.items || []); } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl p-6 border border-cyan-200 bg-cyan-50">
        <h3 className="text-lg font-bold mb-1">Catena blocchi della tua azienda</h3>
        <p className="text-sm text-slate-700">Ogni documento ufficiale ancorato genera un hash SHA-256 chained sul precedente. Inalterabile.</p>
      </div>
      <div className="rounded-2xl p-6 bg-white border border-slate-200">
        <h3 className="text-lg font-bold mb-4">Blocchi ({chain.length})</h3>
        <div className="space-y-2 font-mono text-xs" data-testid="blockchain-list">
          {chain.map(b => (
            <div key={b.id} className="p-3 rounded-xl bg-slate-900 text-emerald-300">
              <div className="flex justify-between flex-wrap gap-2">
                <span>#{String(b.block_index).padStart(5,'0')}</span>
                <span className="text-slate-400">{new Date(b.anchored_at).toLocaleString("it-IT")}</span>
              </div>
              <div className="text-slate-400 text-[10px] mt-1">prev: {b.prev_hash.slice(0,32)}...</div>
              <div className="text-emerald-300 text-[10px] break-all">hash: {b.block_hash}</div>
              <div className="text-slate-500 text-[10px] mt-1">doc_type: {b.doc_type} · doc_id: {b.doc_id}</div>
            </div>
          ))}
          {chain.length === 0 && <div className="text-center text-slate-500 py-8 font-sans">Nessun blocco. Ancorra documenti per iniziare la catena.</div>}
        </div>
      </div>
    </div>
  );
}
